// ============================================================================
// Helpers de categorización de productos (cirugía / medicina / sanitario...)
// y de derivación del tipo de pedido. Espejo del helper en el backend
// (`utils.js`) para mantener la misma lógica en validaciones front/back.
//
// Solo aplica a las clínicas 131 y 999999 — el resto ignoran `subfamilia`
// y los pedidos no se tipifican.
// ============================================================================

export const CLIENTES_CON_SUBFAMILIA = new Set([131, 999999]);

/** True si el cliente debe aplicar la lógica de cirugía/medicina. */
export function clienteUsaSubfamilia(clienteId) {
  if (clienteId == null) return false;
  return CLIENTES_CON_SUBFAMILIA.has(parseInt(clienteId));
}

/**
 * Normaliza un texto para comparar: minúsculas y SIN diacríticos.
 * Necesario porque en BD las familias se guardan sin tilde y en mayúsculas
 * ('INFORMATICA'), pero un dato sucio con tilde ('INFORMÁTICA') rompería la
 * comparación directa: 'informática' !== 'informatica'.
 */
function normalizar(texto) {
  return String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Familias (normalizadas) que mapean a cada categoría de producto.
 * `uniformes` acepta DOS alias: 'vestuario' es el valor real en producción
 * (proveedor MELAN VESTUARI LABORAL) y 'uniformes' se admite por si algún día
 * se normaliza el dato. No dependemos de un único string.
 */
export const FAMILIAS_POR_CATEGORIA = {
  limpieza:    ['limpieza'],
  oficina:     ['oficina'],
  uniformes:   ['vestuario', 'uniformes'],
  informatica: ['informatica'],
};

/**
 * Categoría visible para un producto.
 * Devuelve 'cirugia' | 'medicina' | 'limpieza' | 'oficina' | 'uniformes'
 *        | 'informatica' | 'sanitario'.
 *
 * Uniformes e Informática son familias (texto libre en `producto.familia`),
 * igual que Limpieza y Oficina — NO son subfamilias (la columna `subfamilia`
 * es un enum('cirugia','medicina') y no se toca).
 */
export function categoriaProducto(producto) {
  if (!producto) return 'sanitario';
  const sub = normalizar(producto.subfamilia);
  if (sub === 'cirugia')  return 'cirugia';
  if (sub === 'medicina') return 'medicina';
  const fam = normalizar(producto.familia);
  if (!fam) return 'sanitario';
  // Coincidencia EXACTA, no `includes`. Con `includes` la familia real
  // 'MATERIAL DE OFICINA' pasaría a clasificarse como 'oficina', y como
  // Medicina + Oficina está bloqueado, dejarían de poder pedirse junto a
  // productos de Medicina. Hoy son 'sanitario' y así deben seguir: aquí solo
  // añadimos categorías, no cambiamos las existentes.
  for (const [categoria, alias] of Object.entries(FAMILIAS_POR_CATEGORIA)) {
    if (alias.includes(fam)) return categoria;
  }
  return 'sanitario';
}

/** Etiqueta legible para mostrar en columnas "Tipo" del listado. */
export function etiquetaCategoria(cat) {
  switch (cat) {
    case 'cirugia':     return 'Cirugía';
    case 'medicina':    return 'Medicina';
    case 'limpieza':    return 'Limpieza';
    case 'oficina':     return 'Oficina';
    case 'uniformes':   return 'Uniformes';
    case 'informatica': return 'Informática';
    default:            return 'Sanitario';
  }
}

/**
 * Calcula el tipo de pedido a partir del array de productos.
 *
 * REGLAS DE MEZCLA (sólo clientes 131/999999):
 *   - Cirugía + Medicina           → INCOMPATIBLE
 *   - Medicina + Limpieza/Oficina  → INCOMPATIBLE
 *   - Sanitario + Limpieza/Oficina → OK, tipo Cirugía
 *   - Cirugía  + Limpieza/Oficina  → OK, tipo Cirugía
 *   - Cirugía  + Sanitario         → OK, tipo Cirugía
 *   - Medicina + Sanitario         → OK, tipo Medicina
 *   - Solo Sanitario               → el usuario debe elegir
 *   - Uniformes / Informática      → COMPATIBLES CON TODO: no bloquean ninguna
 *                                    mezcla (ni con Cirugía, ni con Medicina),
 *                                    por eso NO aparecen en `tieneLimpOf`.
 *
 * Resultado:
 *   { tipo: 'cirugia_plastica' | 'medicina_estetica' }
 *   { incompatible: true }                          → mezcla bloqueante
 *   { requiereEleccion: true }                      → solo sanitario genérico
 *   { tipo: null }                                  → pedido vacío / sin tipificar
 */
export function tipoDePedidoSegunProductos(productos) {
  if (!Array.isArray(productos) || productos.length === 0) return { tipo: null };
  const cats = new Set(productos.map(categoriaProducto));
  const tieneCirugia  = cats.has('cirugia');
  const tieneMedicina = cats.has('medicina');
  const tieneLimpOf   = cats.has('limpieza') || cats.has('oficina');
  // Cirugía + Medicina bloqueado.
  if (tieneCirugia && tieneMedicina) return { incompatible: true };
  // Medicina + Limpieza/Oficina bloqueado.
  if (tieneMedicina && tieneLimpOf)  return { incompatible: true };
  if (tieneCirugia)  return { tipo: 'cirugia_plastica' };
  if (tieneMedicina) return { tipo: 'medicina_estetica' };
  // Sin subfamilia específica:
  //   - solo sanitario genérico → el usuario debe elegir
  //   - mezcla con limpieza/oficina → default cirugía
  if (cats.size === 1 && cats.has('sanitario')) {
    return { requiereEleccion: true };
  }
  // OJO (comportamiento heredado, NO es un bug nuevo): un pedido compuesto
  // SOLO por categorías de familia (solo limpieza, solo oficina, solo
  // uniformes, solo informática, o mezcla entre ellas) no entra en el `if`
  // anterior y cae aquí → tipo 'cirugia_plastica' por defecto. Es exactamente
  // lo que ya hacía hoy un pedido solo de limpieza; se deja igual a propósito.
  return { tipo: 'cirugia_plastica' };
}

/**
 * Comprueba si añadir `nuevo` al carrito `actuales` rompe la compatibilidad.
 * Devuelve { incompatible: true, motivo } o null si OK.
 *
 * Conflictos detectados:
 *   1. Cirugía  ↔ Medicina
 *   2. Medicina ↔ Limpieza / Oficina   (en ambas direcciones)
 *
 * Uniformes e Informática NO generan ningún conflicto: son compatibles con
 * todo y por eso están deliberadamente ausentes de las comprobaciones.
 */
export function compatibilidadAlAnadirProducto(actuales, nuevo) {
  const catNuevo = categoriaProducto(nuevo);
  const catsActuales = new Set((actuales || []).map(categoriaProducto));

  // 1. Cirugía ↔ Medicina
  if (catNuevo === 'cirugia' && catsActuales.has('medicina')) {
    return {
      incompatible: true,
      motivo: 'Este producto es de Cirugía pero el pedido ya contiene productos de Medicina. Elimínalos del pedido antes de añadir este.',
    };
  }
  if (catNuevo === 'medicina' && catsActuales.has('cirugia')) {
    return {
      incompatible: true,
      motivo: 'Este producto es de Medicina pero el pedido ya contiene productos de Cirugía. Elimínalos del pedido antes de añadir este.',
    };
  }

  // 2. Medicina ↔ Limpieza / Oficina
  if (catNuevo === 'medicina' && (catsActuales.has('limpieza') || catsActuales.has('oficina'))) {
    return {
      incompatible: true,
      motivo: 'Este producto es de Medicina pero el pedido ya contiene productos de Limpieza u Oficina. Elimínalos antes de añadir este.',
    };
  }
  if ((catNuevo === 'limpieza' || catNuevo === 'oficina') && catsActuales.has('medicina')) {
    const nuevoLbl = catNuevo === 'limpieza' ? 'Limpieza' : 'Oficina';
    return {
      incompatible: true,
      motivo: `Este producto es de ${nuevoLbl} pero el pedido ya contiene productos de Medicina. Elimínalos antes de añadir este.`,
    };
  }

  return null;
}
